import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, 
  Send, 
  Bot, 
  User, 
  RefreshCw,
  Scale,
  AlertCircle,
  Clock,
  ChevronRight
} from 'lucide-react';
import { legalAPI } from '../services/api';
import MarkdownRenderer from './MarkdownRenderer';
import toast from 'react-hot-toast';
import '../styles/LegalGuidance.css';

const LegalGuidance = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Welcome message and initial setup
  useEffect(() => {
    initializeChat();
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const initializeChat = () => {
    const welcomeMessage = {
      id: 'welcome',
      type: 'bot',
      content: `Welcome to Legal Guidance! 👋

I'm your AI Legal Counsellor, specialized in Indian law and legal matters. I'm here to help you understand legal concepts, procedures, and provide guidance on various legal issues.

**What I can help you with:**
• Legal procedures and documentation
• Understanding Indian laws and regulations
• Legal rights and obligations
• Court procedures and processes
• Contract and agreement guidance
• Property and business law matters

**Please note:** This is for informational purposes only and does not constitute formal legal advice. For specific legal matters, please consult with a qualified lawyer.

How can I assist you with your legal queries today?`,
      timestamp: new Date(),
      isWelcome: true
    };

    setMessages([welcomeMessage]);
    
    // Set initial suggested questions
    setSuggestedQuestions([
      "What are my rights as a tenant in India?",
      "How do I register a complaint with consumer court?",
      "What documents are needed for property registration?",
      "How to file for divorce in India?",
      "What are the labor laws for employees in India?"
    ]);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (messageText = null, useStreaming = true) => {
    const message = messageText || inputMessage.trim();
    if (!message || isLoading) return;

    // Clear input and add user message
    setInputMessage('');
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setIsTyping(true);
    setSuggestedQuestions([]); // Clear suggestions when user sends message

    // Get last 6 messages for context (excluding welcome message)
    const recentMessages = messages.filter(msg => !msg.isWelcome).slice(-6);
    const conversationContext = recentMessages.map(msg => ({
      role: msg.type === 'user' ? 'user' : 'assistant',
      content: msg.content
    }));

    // Create initial bot message for streaming
    const botMessageId = Date.now() + 1;
    const initialBotMessage = {
      id: botMessageId,
      type: 'bot',
      content: '',
      timestamp: new Date(),
      isStreaming: true
    };

    setMessages(prev => [...prev, initialBotMessage]);

    if (useStreaming) {
      // Use streaming API
      legalAPI.getLegalGuidanceStream(
        {
          message,
          conversationContext,
          conversationId
        },
        // onChunk - handle streaming content
        (data) => {
          setMessages(prev => prev.map(msg => 
            msg.id === botMessageId 
              ? { ...msg, content: data.fullContent || data.content }
              : msg
          ));
        },
        // onComplete - handle completion
        (data) => {
          setMessages(prev => prev.map(msg => 
            msg.id === botMessageId 
              ? { 
                  ...msg, 
                  content: data.fullResponse,
                  isStreaming: false,
                  suggestedQuestions: data.suggestedQuestions || []
                }
              : msg
          ));
          
          // Update suggested questions
          if (data.suggestedQuestions && data.suggestedQuestions.length > 0) {
            setSuggestedQuestions(data.suggestedQuestions);
          }

          // Set conversation ID if provided
          if (data.conversationId) {
            setConversationId(data.conversationId);
          }

          setIsLoading(false);
          setIsTyping(false);
        },
        // onError - handle errors
        (error) => {
          console.error('Legal guidance streaming error:', error);
          
          setMessages(prev => prev.map(msg => 
            msg.id === botMessageId 
              ? { 
                  ...msg, 
                  content: "I apologize, but I'm having trouble processing your request right now. Please try again in a moment.",
                  isStreaming: false,
                  isError: true
                }
              : msg
          ));
          
          toast.error('Failed to get legal guidance');
          setIsLoading(false);
          setIsTyping(false);
        }
      );
    } else {
      // Fallback to non-streaming API
      try {
        const response = await legalAPI.getLegalGuidance({
          message,
          conversationContext,
          conversationId
        });

        setMessages(prev => prev.map(msg => 
          msg.id === botMessageId 
            ? { 
                ...msg, 
                content: response.response,
                isStreaming: false,
                suggestedQuestions: response.suggestedQuestions || []
              }
            : msg
        ));
        
        // Update suggested questions
        if (response.suggestedQuestions && response.suggestedQuestions.length > 0) {
          setSuggestedQuestions(response.suggestedQuestions);
        }

        // Set conversation ID if provided
        if (response.conversationId) {
          setConversationId(response.conversationId);
        }

      } catch (error) {
        console.error('Legal guidance error:', error);
        
        setMessages(prev => prev.map(msg => 
          msg.id === botMessageId 
            ? { 
                ...msg, 
                content: "I apologize, but I'm having trouble processing your request right now. Please try again in a moment.",
                isStreaming: false,
                isError: true
              }
            : msg
        ));
        
        toast.error('Failed to get legal guidance');
      } finally {
        setIsLoading(false);
        setIsTyping(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearConversation = () => {
    setMessages([]);
    setConversationId(null);
    setSuggestedQuestions([]);
    initializeChat();
    toast.success('Conversation cleared');
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="legal-guidance-container">
      {/* Header */}
      <div className="chat-header">
        <div className="header-content">
          <div className="header-title">
            <Scale size={24} className="header-icon" />
            <div>
              <h1>Legal Guidance</h1>
              <p>AI-powered legal counselling for Indian law</p>
            </div>
          </div>
          
          <button 
            onClick={clearConversation}
            className="clear-btn"
            title="Clear conversation"
          >
            <RefreshCw size={16} />
            Clear Chat
          </button>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="chat-messages">
        {messages.map((message) => (
          <div 
            key={message.id} 
            className={`message ${message.type} ${message.isWelcome ? 'welcome' : ''} ${message.isError ? 'error' : ''} ${message.isStreaming ? 'streaming' : ''}`}
          >
            <div className="message-avatar">
              {message.type === 'user' ? (
                <User size={20} />
              ) : (
                <Bot size={20} />
              )}
            </div>
            
            <div className="message-content">
              <div className="message-bubble">
                {message.type === 'bot' ? (
                  <MarkdownRenderer content={message.content} />
                ) : (
                  <p>{message.content}</p>
                )}
              </div>
              
              <div className="message-meta">
                <Clock size={12} />
                <span>{formatTime(message.timestamp)}</span>
              </div>
            </div>
          </div>
        ))}

        {/* Typing Indicator - Only show when not streaming and is typing */}
        {isTyping && !messages.some(msg => msg.isStreaming) && (
          <div className="message bot typing">
            <div className="message-avatar">
              <Bot size={20} />
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Questions */}
      {suggestedQuestions.length > 0 && (
        <div className="suggested-questions">
          <p className="suggestions-title">Suggested follow-up questions:</p>
          <div className="suggestions-list">
            {suggestedQuestions.map((question, index) => (
              <button
                key={index}
                onClick={() => handleSendMessage(question)}
                className="suggestion-btn"
                disabled={isLoading}
              >
                <ChevronRight size={14} />
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="chat-input-area">
        <div className="input-container">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me any legal question..."
            className="message-input"
            disabled={isLoading}
            rows={1}
          />
          
          <button
            onClick={() => handleSendMessage()}
            disabled={!inputMessage.trim() || isLoading}
            className="send-btn"
          >
            {isLoading ? (
              <RefreshCw size={20} className="animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>
        
        <div className="input-disclaimer">
          <AlertCircle size={12} />
          <span>For informational purposes only. Not a substitute for professional legal advice.</span>
        </div>
      </div>

    </div>
  );
};

export default LegalGuidance;
